import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/AuthShell";
import { useAuth, formatApiError } from "@/lib/auth";
import { applyUiLanguage } from "@/lib/uiLang";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "merchant" && user.language) applyUiLanguage(user.language);
      toast.success(t("auth.welcome_toast", { name: user.name }));
      navigate(user.role === "admin" ? "/admin" : "/merchant");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <form onSubmit={submit} data-testid="login-form">
        <h1 className="text-3xl font-semibold tracking-tight">{t("auth.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("auth.subtitle")}</p>

        <div className="mt-8 space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs font-medium">
              {t("auth.email")}
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5 h-11 rounded-xl"
              data-testid="login-email-input"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs font-medium">
                {t("auth.password")}
              </Label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                data-testid="login-forgot-link"
              >
                {t("auth.forgot_link")}
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1.5 h-11 rounded-xl"
              data-testid="login-password-input"
              placeholder="••••••••"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="mt-6 w-full h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90"
          data-testid="login-submit-btn"
        >
          {loading ? t("auth.loading") : t("auth.login")}
          {!loading && <ArrowRight className="ml-1.5 h-4 w-4" />}
        </Button>
      </form>
    </AuthShell>
  );
}
